/*
 * (c) copyright 2023 International Road Dynamics Inc. and related companies.
 * All rights reserved.
 * Copyright as an unpublished work.
 *
 * This file contains confidential information of International
 * Road Dynamics Inc. and related companies, and should be treated as proprietary
 * and confidential.
 *
 * The use, copying, disclosure or modification of the
 * programs and/or source code is prohibited unless
 * otherwise provided for in the license or confidentiality
 * agreements.
 *
 * Any copy must contain this copyright notice.
 */

import React from 'react'
import { DateTime } from 'luxon'
import {
  Container,
  Grid,
  Snackbar
} from '@material-ui/core'
import {
  createMuiTheme,
  ThemeProvider
} from '@material-ui/core/styles'
import Alert from '@material-ui/lab/Alert'
import escapeRegExp from 'lodash/escapeRegExp'

import { getSitesData } from './api/Site'
import { SiteTypes } from './constants'
import PageHeader from './ui/PageHeader/PageHeader'
import PageFooter from './ui/PageFooter/PageFooter'
import Search from './components/Search/Search'
import MapContainer from './containers/MapContainer/MapContainer'

class App extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      isSnackbarOpened: false,
      lastUpdated: null,
      sites: [],
      searchTerm: '',
      snackbarMsg: ''
    }

    this.closeSnackbar = this.closeSnackbar.bind(this)
    this.handleSearchTermChange = this.handleSearchTermChange.bind(this)
    this.theme = createMuiTheme({
      typography: {
        fontFamily: 'inherit'
      }
    })
  }

  componentDidMount () {
    this.fetchSites()
  }

  /**
   * Function to close the snackbar.
   */
  closeSnackbar () {
    this.setState({
      isSnackbarOpened: false
    })
  }

  async fetchSites () {
    try {
      const { lastUpdated, sites } = await getSitesData()

      if (lastUpdated && sites) {
        this.setState({
          lastUpdated,
          sites: sites.map((site, index) => Object.assign({}, site, { id: index + 1 }))
        })
      } else {
        this.openSnackbar('No site data available')
      }
    } catch (e) {
      this.openSnackbar('No site data available')
    }
  }

  getSitesBySearchTerm (searchTerm)
  {
    let sites

    if (searchTerm.length === 0)
    {
      sites = this.state.sites.slice()
    }
    else
    {
      sites = this.state.sites.filter(site => {
        let isMatch = false
        const searchRegex = new RegExp(escapeRegExp(searchTerm), 'i')

        if (site.name.search(searchRegex) !== -1)
        {
          isMatch = true
        }
        else if (site.address.search(searchRegex) !== -1)
        {
          isMatch = true
        }
        else if (site.island.search(searchRegex) !== -1)
        {
          isMatch = true
        }
        else if (site.latitude.toString().search(searchRegex) !== -1)
        {
          isMatch = true
        }
        else if (site.longitude.toString().search(searchRegex) !== -1)
        {
          isMatch = true
        }
        else if (SiteTypes[site.type] && SiteTypes[site.type].search(searchRegex) !== -1)
        {
          isMatch = true
        }

        return isMatch
      })
    }

    return sites
  }

  handleSearchTermChange (event) {
    this.setState({ searchTerm: event.target.value })
  }

  /**
   * Function to open snackbar and show the specified message
   *
   * @param {String} snackbarMsg message to show in snack bar
   */
  openSnackbar (snackbarMsg) {
    this.setState({
      isSnackbarOpened: true,
      snackbarMsg
    })
  }

  render () {
    const visibleSites = this.getSitesBySearchTerm(this.state.searchTerm)

    return (
      <>
        <ThemeProvider theme={this.theme}>
          <Snackbar
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            autoHideDuration={10000}
            message={this.state.snackbarMsg}
            onClose={this.closeSnackbar}
            open={this.state.isSnackbarOpened}
          >
            <Alert onClose={this.closeSnackbar} severity="info">
              {this.state.snackbarMsg}
            </Alert>
          </Snackbar>

          <PageHeader vi2mLoginUrl={process.env.REACT_APP_VI2M_LOGIN_URL} />

          <Container maxWidth={false}>
            <div id="mainBody">
              <Search value={this.state.searchTerm} onChange={this.handleSearchTermChange} />

              <Grid
                container
                direction="row"
                justify="space-between"
                alignItems="center"
              >
                <Grid item>
                  <p id="pNumOfVisibleSites">Showing {visibleSites.length} of {this.state.sites.length} sites</p>
                </Grid>

                {this.state.lastUpdated &&
                <Grid item>
                  <p id="pLastUpdated">Last updated: {DateTime.fromISO(this.state.lastUpdated).toFormat('yyyy-MMM-dd h:mm a')}</p>
                </Grid>
                }
              </Grid>

              <MapContainer sites={visibleSites} />
            </div>
          </Container>

          <PageFooter />
        </ThemeProvider>
      </>
    )
  }
}

export default App
